<template>
  <div>
    <div class="toolbar">
      <h2>用户数据</h2>
      <el-button @click="load">刷新</el-button>
    </div>

    <el-row v-loading="statsLoading" :gutter="16" class="stat-row">
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value">{{ stats.total ?? 0 }}</div>
          <div class="stat-label">用户总数</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value accent">{{ stats.new_today ?? 0 }}</div>
          <div class="stat-label">今日新增</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value accent">{{ stats.new_week ?? 0 }}</div>
          <div class="stat-label">近7日新增</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value success">{{ stats.active_today ?? 0 }}</div>
          <div class="stat-label">今日活跃</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value success">{{ stats.active_week ?? 0 }}</div>
          <div class="stat-label">近7日活跃</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value warning">{{ stats.clicks_today ?? 0 }}</div>
          <div class="stat-label">今日点击</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value warning">{{ stats.clicks_week ?? 0 }}</div>
          <div class="stat-label">近7日点击</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value">{{ stats.clicks_total ?? 0 }}</div>
          <div class="stat-label">累计点击</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value">{{ stats.merchant_views ?? 0 }}</div>
          <div class="stat-label">商家浏览量</div>
        </el-card>
      </el-col>
    </el-row>

    <div class="filters">
      <el-input
        v-model="keyword"
        placeholder="搜索用户 ID / 用户名 / 昵称"
        clearable
        prefix-icon="el-icon-search"
        class="filter-input"
        @keyup.enter.native="onSearch"
      />
      <el-button type="primary" @click="onSearch">搜索</el-button>
    </div>

    <div v-if="!isMobile" class="table-scroll">
      <el-table :data="list" v-loading="loading" border>
        <el-table-column prop="user_id" label="用户 ID" width="120" />
        <el-table-column label="用户名" width="140">
          <template slot-scope="{ row }">{{ row.username ? '@' + row.username : '-' }}</template>
        </el-table-column>
        <el-table-column prop="nickname" label="昵称" min-width="120" />
        <el-table-column label="语言" width="90">
          <template slot-scope="{ row }">{{ formatLang(row.language) }}</template>
        </el-table-column>
        <el-table-column label="城市" width="100">
          <template slot-scope="{ row }">{{ row.city_name_zh || '未选择' }}</template>
        </el-table-column>
        <el-table-column label="注册时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="最后活跃" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.last_active_at) }}</template>
        </el-table-column>
        <el-table-column prop="click_count" label="点击量" width="90" align="right" />
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="page"
        @current-change="loadList"
      />
    </div>

    <MobileCardList
      v-if="isMobile"
      :data="list"
      :loading="loading"
      :fields="cardFields"
    >
      <template #username="{ row }">{{ row.username ? '@' + row.username : '-' }}</template>
      <template #language="{ row }">{{ formatLang(row.language) }}</template>
      <template #city_name_zh="{ row }">{{ row.city_name_zh || '未选择' }}</template>
      <template #created_at="{ row }">{{ formatTime(row.created_at) }}</template>
      <template #last_active_at="{ row }">{{ formatTime(row.last_active_at) }}</template>
    </MobileCardList>

    <el-pagination
      v-if="isMobile"
      class="pager"
      small
      background
      layout="total, prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page.sync="page"
      @current-change="loadList"
    />
  </div>
</template>

<script>
import { api } from '../api';
import responsiveMixin from '../mixins/responsive';
import { formatDateTime } from '../utils/listHelpers';

const emptyStats = () => ({
  total: 0,
  new_today: 0,
  new_week: 0,
  active_today: 0,
  active_week: 0,
  clicks_total: 0,
  clicks_today: 0,
  clicks_week: 0,
  merchant_views: 0,
});

export default {
  name: 'Users',
  mixins: [responsiveMixin],
  data() {
    return {
      stats: emptyStats(),
      statsLoading: false,
      list: [],
      loading: false,
      keyword: '',
      page: 1,
      pageSize: 20,
      total: 0,
      cardFields: [
        { label: '用户 ID', prop: 'user_id' },
        { label: '用户名', prop: 'username' },
        { label: '昵称', prop: 'nickname' },
        { label: '语言', prop: 'language' },
        { label: '城市', prop: 'city_name_zh' },
        { label: '注册时间', prop: 'created_at' },
        { label: '最后活跃', prop: 'last_active_at' },
        { label: '点击量', prop: 'click_count' },
      ],
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      await Promise.all([this.loadStats(), this.loadList()]);
    },
    async loadStats() {
      this.statsLoading = true;
      try {
        const { data } = await api.users.stats();
        this.stats = { ...emptyStats(), ...data };
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.statsLoading = false;
      }
    },
    async loadList() {
      this.loading = true;
      try {
        const { data } = await api.users.list({
          page: this.page,
          pageSize: this.pageSize,
          keyword: this.keyword.trim() || undefined,
        });
        this.list = data.list;
        this.total = data.total;
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.loadList();
    },
    formatLang(lang) {
      if (lang === 'zh') return '中文';
      if (lang === 'my') return '缅文';
      return '未选择';
    },
    formatTime(val) {
      return formatDateTime(val);
    },
  },
};
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.toolbar h2 { margin: 0; }
.stat-row { margin-bottom: 20px; }
.stat-card { margin-bottom: 16px; text-align: center; }
.stat-value { font-size: 28px; font-weight: 700; color: #303133; line-height: 1.2; }
.stat-value.accent { color: #409eff; }
.stat-value.success { color: #67c23a; }
.stat-value.warning { color: #e6a23c; }
.stat-label { margin-top: 8px; font-size: 13px; color: #909399; }
.filters { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
.filter-input { width: 280px; max-width: 100%; }
.pager { margin-top: 16px; text-align: right; }
</style>
